import { useQuery } from "@tanstack/react-query";
import { FC, useState } from "react";
import styled, { useTheme } from "styled-components";
import { EditIcon, TrashIcon } from "lucide-react";
import UserRolesAPI from "@/api/UserRoles/UserRoles.js";
import {
  Button,
  Flyout,
  FlyoutHeader,
  FlyoutTitle,
} from "@monolith-forensics/monolith-ui";

import { UserTab } from "@/pages/Users/components/TabSection.js";
import { DeleteUserRoleModal, EditUserRoleModal } from "../Modals";
import PermissionsTab from "@/pages/Users/components/UserRoles/PermissionsTab";
import { UserRoleDefs } from "../ColumnDefinitions";

interface UserRoleFlyoutProps {
  show: boolean;
  onClose?: () => void;
  defaultInfo: any;
  uuid: string;
  onEdit: (data: any) => void;
  onDelete: (data: any) => void;
}

type Tab = {
  label: string;
  value: string;
};

const tabDefs: Tab[] = [
  {
    label: "Details",
    value: "details",
  },
  {
    label: "Permissions",
    value: "permissions",
  },
];

const UserRoleFlyout: FC<UserRoleFlyoutProps> = ({
  show = false,
  onClose,
  defaultInfo,
  uuid,
  onEdit,
  onDelete,
}) => {
  const theme: any = useTheme();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["user-roles", "list", { uuid }],
    queryFn: ({ queryKey }) => {
      const [_, __, query] = queryKey;
      return UserRolesAPI.get(query);
    },
    enabled: !!uuid,
    initialData: [defaultInfo],
  });

  const itemData = data?.data?.[0] || {};

  return (
    <Flyout open={show} onClose={() => onClose?.()}>
      {isLoading && <div>Loading...</div>}
      {!isLoading && (
        <>
          <FlyoutHeader>
            <FlyoutTitle>{itemData.name}</FlyoutTitle>
            {!itemData.protected && (
              <EditUserRoleModal
                show={showEditModal}
                defaultFormData={{
                  uuid: itemData?.uuid,
                  name: itemData?.name,
                  description: itemData?.description,
                }}
                onClose={() => setShowEditModal(false)}
                onCancel={() => setShowEditModal(false)}
                onSubmit={(data: any) => {
                  refetch();
                  onEdit?.(data);
                  setShowEditModal(false);
                }}
              />
            )}
            {!itemData.protected && (
              <DeleteUserRoleModal
                show={showDeleteModal}
                defaultFormData={{ ...itemData }}
                onClose={() => setShowDeleteModal(false)}
                onCancel={() => setShowDeleteModal(false)}
                onSubmit={(data: any) => {
                  onDelete?.(data);
                  setShowDeleteModal(false);
                }}
              />
            )}
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: 10,
                alignItems: "center",
                color: theme.palette.text.secondary,
                fontWeight: 500,
                marginBottom: 5,
              }}
            >
              <div>User Role</div>
              {itemData.protected && (
                <>This is a default user role and cannot be edited.</>
              )}
              {!itemData.protected && (
                <Button
                  size="xxs"
                  variant="text"
                  leftSection={<EditIcon size={14} />}
                  color="primary"
                  onClick={() => setShowEditModal(true)}
                >
                  Edit
                </Button>
              )}
              {!itemData.protected && (
                <Button
                  size="xxs"
                  variant="text"
                  leftSection={<TrashIcon size={14} />}
                  color="error"
                  onClick={() => setShowDeleteModal(true)}
                >
                  Delete
                </Button>
              )}
            </div>
          </FlyoutHeader>
          <Tabs>
            <TabPanel value="details">
              <FlyoutDetailsTab defaultData={itemData} />
            </TabPanel>
            <TabPanel value="permissions">
              <PermissionsTab defaultData={itemData} />
            </TabPanel>
          </Tabs>
        </>
      )}
    </Flyout>
  );
};

const TabPanel = styled(({ className, children }) => {
  return <div className={className}>{children}</div>;
})`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
`;

TabPanel.displayName = "TabPanel";

const Tabs = styled(({ className, children, defaultValue, onChange }) => {
  if (!Array.isArray(children)) children = [children]; // Ensure children is an array
  const [activeTab, setActiveTab] = useState(defaultValue || "details");

  const handleChange = (tab: UserTab) => {
    setActiveTab(tab);
    onChange?.(tab);
  };

  const tabPanels = children.filter(
    (child: any) => child?.type?.displayName === "TabPanel"
  );

  const activePanel = tabPanels.find(
    (child: any) => child.props.value === activeTab
  );

  return (
    <div className={className}>
      <div className="tabs-list">
        {tabDefs.map((tab: any) => (
          <div
            className={`tab`}
            data-active={activeTab === tab.value}
            onClick={() => handleChange(tab.value)}
            key={tab.value}
          >
            {tab.label}
          </div>
        ))}
      </div>
      <div className="tab-content">{activePanel}</div>
    </div>
  );
})`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;

  .tabs-list {
    user-select: none;
    cursor: pointer;
    display: flex;
    flex-direction: row;
    color: ${({ theme }) => theme.palette.text.secondary};
    border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
  }

  .tab {
    padding-right: 10px;
    padding-left: 10px;
    padding-top: 5px;
    padding-bottom: 5px;
    border-bottom: 1px solid transparent;
  }

  .tab:hover {
    background-color: ${({ theme }) => theme.palette.action.hover};
  }

  .tab[data-active="true"] {
    border-bottom-color: ${({ theme }) => theme.palette.primary.main};
    color: ${({ theme }) => theme.palette.primary.main};
  }

  .tab-content {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    height: 0px;
  }
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const DetailLabel = styled.div`
  font-weight: 500;
  margin-right: 8px;
  min-width: 125px;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

const DetailValue = styled.div`
  white-space: pre-wrap;
  max-height: 150px;
  overflow-y: auto;
`;

const MainDetails = styled(({ className, defaultData }) => {
  return (
    <div className={className}>
      <h3>User Role Details</h3>
      <div className="event-details">
        {UserRoleDefs.filter((col) => col.dataField !== "description").map(
          (col) => {
            const key = col.dataField;
            const label = col.caption;
            const value = col?.render
              ? col.render(defaultData)
              : defaultData[key];

            return (
              <DetailItem key={key}>
                <DetailLabel>{label}</DetailLabel>
                <DetailValue>{value}</DetailValue>
              </DetailItem>
            );
          }
        )}
        <DetailItem>
          <DetailLabel>Description</DetailLabel>
        </DetailItem>
        <DetailValue>{defaultData.description || "None"}</DetailValue>
      </div>
    </div>
  );
})`
  display: flex;
  flex-direction: column;

  h3 {
    margin: 0px;
    margin-bottom: 10px;
  }

  .event-details {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .trace-attributes-list {
    font-size: 12px;
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    margin-top: 10px;
  }

  .attribute-type {
    font-weight: 550;
    color: ${({ theme }) => theme.palette.text.secondary};
  }
`;

const FlyoutDetailsTab = styled(({ className, defaultData }) => {
  return (
    <div className={className}>
      <MainDetails defaultData={defaultData} />
    </div>
  );
})`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  height: 0px;

  padding: 10px 0px;
`;

export default UserRoleFlyout;
